//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
import 'fast-text-encoding'; /* MINIMAL TextEncoder polyfill! Only supports utf-8 encoding. */
import * as base64 from 'base64-js';
import logger from 'app/logger/logger';

export enum OAuthCsrfSalt {
  Facebook = 'FacebookOAuth',
}

/**
 * Calculate the storage key to be used with this Csrf salt
 * @param salt A static string that is unique to this particular use case of the CSRF token.
 */
function getStorageKey(salt: OAuthCsrfSalt): string {
  return `crl8.lastCsrf${salt}`;
}

/**
 * Creates a CSRF token we can use in the OAuth process
 * to help us ensure that any OAuth callbacks we process
 *   1. are created by the current user and
 *   2. are intended for the current client
 * @param salt A static string that is unique to this particular use case of the CSRF token.
 * @return Promise resolving to a string csrf token
 */
export function deriveOAuthCsrfToken(salt: OAuthCsrfSalt): Promise<string> {
  // CSRF Tokens should contain information only the user & our system can know.
  // Validating this Csrf token should mean that this exact user recently made
  // this request, for this client.
  //
  // To give it this meaning, we hash the clientId, userId, and api gateway token.
  // The api gateway token rotates every few hours, so replay attacks on this endpoint
  // won't be very viable.
  //
  // By using a hash, we avoid giving Facebook a copy of our users' Api tokens.
  //
  // We also add a salt to differentiate between use cases of the CSRF token.
  // e.g., Facebook OAuth & Instagram OAuth should generate different CSRF tokens,
  // even for the same user around the same time.
  const nonce = crypto.getRandomValues(new Uint8Array(64));
  const token = base64.fromByteArray(nonce);
  const storageValue = JSON.stringify({
    token,
    issued: +new Date(),
  });
  // Overwriting the previous value is fine
  localStorage.setItem(getStorageKey(salt), storageValue);

  // Previous implementation used async; let's keep using the promise API for consistency.
  return Promise.resolve(token);
}

function getStoredOAuthCsrfToken(salt: OAuthCsrfSalt): string | undefined {
  const encoded = localStorage.getItem(getStorageKey(salt));
  if (!encoded) return undefined;
  try {
    const data = JSON.parse(encoded) as { token: string; issued: number };
    if (+new Date() - data.issued > 60 * 15 * 1000) {
      // If the stored token is more than 15 minutes old, we don't trust it.
      return undefined;
    }
    // Token should be valid, let's return it.
    return data.token;
  } catch (e) {
    logger.console.warn('Failed to decode OAuth CSRF token from storage', e);
    return undefined;
  }
}

/**
 * Checks that the CSRF Token is valid for the current user and client.
 * Throws an exception if it is not valid.
 */
export function assertValidOAuthCsrfToken(
  csrfToken: string,
  salt: OAuthCsrfSalt,
): Promise<void> {
  if (!csrfToken) {
    return Promise.reject(new Error('Input CSRF Token is not valid'));
  }
  const storedToken = getStoredOAuthCsrfToken(salt);
  if (!storedToken) {
    return Promise.reject(new Error('Stored CSRF Token is not valid'));
  }
  if (storedToken !== csrfToken) {
    return Promise.reject(new Error('CSRF Token invalid'));
  }
  return Promise.resolve();
}
